// Centralized API Configuration
// Provides base URLs, controller names, and endpoint builders

// Base API configuration
export const API_CONFIG = {
  BASE_URL: '/api/v1',
  CONTROLLERS: {
    ADDRESSES: 'addresses',
    AUTH: 'auth',
    BROKER_ROUTES: 'broker-routes',
    CONSTANTS: 'constants',
    DEVICE_IMAGES: 'device-images',
    OPT_INS: 'opt-ins',
    PHONE_VALIDATIONS: 'phone-validations',
    ROUTE_STOPS: 'route-stops',
    ROUTES: 'routes',
    SHIPMENTS: 'shipments',
    SYNC: 'sync',
    USERS: 'users',
  },
} as const;

// Endpoint builders
export const buildEndpoint = (controller: string, path: string = ''): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const pathSuffix = cleanPath ? `/${cleanPath}` : '';
  return `${API_CONFIG.BASE_URL}/${controller}${pathSuffix}`;
};

// Specific endpoint builders for each controller
export const authEndpoints = {
  GET_ME: () => buildEndpoint(API_CONFIG.CONTROLLERS.AUTH, 'me'),
  LOGIN_REQUEST: () => buildEndpoint(API_CONFIG.CONTROLLERS.AUTH, 'login-request'),
  LOGOUT: () => buildEndpoint(API_CONFIG.CONTROLLERS.AUTH, 'logout'),
  REFRESH: () => buildEndpoint(API_CONFIG.CONTROLLERS.AUTH, 'refresh'),
  VERIFY_OTP: () => buildEndpoint(API_CONFIG.CONTROLLERS.AUTH, 'verify-otp'),
} as const;

export const routesEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.ROUTES),
  DETAIL: (id: string | number) => buildEndpoint(API_CONFIG.CONTROLLERS.ROUTES, id.toString()),
} as const;

export const brokerRoutesEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.BROKER_ROUTES),
} as const;

export const routeStopsEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.ROUTE_STOPS),
} as const;

export const shipmentsEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.SHIPMENTS),
  DETAIL: (id: string | number) => buildEndpoint(API_CONFIG.CONTROLLERS.SHIPMENTS, id.toString()),
  BY_ROUTE: (routeId: string | number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.SHIPMENTS, `routes/${routeId.toString()}`),
} as const;

export const usersEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.USERS),
  DETAIL: (id: string | number) => buildEndpoint(API_CONFIG.CONTROLLERS.USERS, id.toString()),
} as const;

export const constantsEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.CONSTANTS),
} as const;

export const optInEndpoints = {
  SUBMIT: () => buildEndpoint(API_CONFIG.CONTROLLERS.OPT_INS),
} as const;

export const phoneValidationEndpoints = {
  VALIDATE: (uuid: string) => buildEndpoint(API_CONFIG.CONTROLLERS.PHONE_VALIDATIONS, uuid),
  HEALTH: () => buildEndpoint(API_CONFIG.CONTROLLERS.PHONE_VALIDATIONS, 'health'),
} as const;

export const deviceImageEndpoints = {
  LIST_BY_ROUTE: (routeId: string | number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.DEVICE_IMAGES, `routes/${routeId.toString()}`),
  LIST_BY_ROUTE_STOP: (routeStopId: string | number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.DEVICE_IMAGES, `route-stops/${routeStopId.toString()}`),
  GET_SAS_URL: (deviceImageId: string | number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.DEVICE_IMAGES, `${deviceImageId.toString()}/sas-url`),
  UPLOAD: (deviceImageTypeId: number, entityId: number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.DEVICE_IMAGES, `${deviceImageTypeId}/${entityId}`),
  DELETE: (deviceImageId: string | number) =>
    buildEndpoint(API_CONFIG.CONTROLLERS.DEVICE_IMAGES, deviceImageId.toString()),
} as const;

export const addressesEndpoints = {
  LIST: () => buildEndpoint(API_CONFIG.CONTROLLERS.ADDRESSES),
  BY_IDS: (ids: number[]) => {
    const queryParams = ids.map((id) => `id=${id}`).join('&');
    return `${buildEndpoint(API_CONFIG.CONTROLLERS.ADDRESSES)}?${queryParams}`;
  },
} as const;

export const syncEndpoints = {
  BROKER_ROUTES: () => buildEndpoint(API_CONFIG.CONTROLLERS.SYNC, 'broker-routes'),
  ROUTE_STOPS: () => buildEndpoint(API_CONFIG.CONTROLLERS.SYNC, 'route-stops'),
  ROUTES: () => buildEndpoint(API_CONFIG.CONTROLLERS.SYNC, 'routes'),
  SHIPMENTS: () => buildEndpoint(API_CONFIG.CONTROLLERS.SYNC, 'shipments'),
} as const;
